import { defineStore } from 'pinia';
import { storeConfig } from '@/shared/storeConfig';
import countryAlpha3 from '@/utils/convertCountry';

import type {
  CountriesList,
  Visitor,
  VisitorCountryTest,
} from '@/types/UserData';

export const useCountriesStore = defineStore(
  'countries',
  () => {
    const browserInfo = import.meta.client ? getBrowserInfo() : undefined;

    const { availableCountries, defaultCountry } = useParameters();

    const allCountriesList = ref<CountriesList[]>();
    const visitorData = ref<Visitor>();

    function filteredCountries(countries: CountriesList[]) {
      const countryCodesToBeRemoved = ['CHN'];

      return countries.filter((country) => {
        return !countryCodesToBeRemoved.includes(country.code);
      });
    }

    async function getCountries() {
      try {
        const { ApiService } = useApiService();

        await ApiService.get<CountriesList[]>('/country').then((res: any) => {
          allCountriesList.value = filteredCountries(res.data);
        });
      } catch (error) {
        console.error('error: ', error);
      }
    }

    async function getVisitorData() {
      try {
        if (isDev() || isStage()) {
          // Modify the code below to the country you want to simulate
          const countryTest = ref<VisitorCountryTest>('BRA');

          visitorData.value = {
            country: countryTest.value,
            country_alpha2: countryTest.value,
          };

          if (import.meta.client) {
            document.documentElement.dataset.country =
              visitorData.value.country;
          }
        } else {
          function resolveApiVisitorData(res: { data: Visitor }) {
            visitorData.value = res.data || {};
            if (res.data.country) {
              const countrAlpha = countryAlpha3(res.data.country);

              visitorData.value.country_alpha2 = res.data.country;
              visitorData.value.country = countrAlpha;
              document.documentElement.dataset.country = countrAlpha;
            }
          }

          if (document.documentElement.dataset.country) {
            resolveApiVisitorData({
              data: {
                country: document.documentElement.dataset.country,
              },
            });
          } else {
            function getFromWorker() {
              const { ApiService } = useApiService();

              return new Promise(function (resolve) {
                setTimeout(async function () {
                  resolve(
                    await ApiService.get<Visitor>('/visitor-info').then(
                      (res: any) => {
                        return resolveApiVisitorData(res);
                      },
                    ),
                  );
                }, 5);
              });
            }

            await getFromWorker();
          }
        }
      } catch (error) {
        console.error('error: ', error);
      }
    }

    const visitorOriginalCountry = computed<string>(() => {
      if (visitorData.value?.country) {
        return visitorData.value?.country;
      } else if (browserInfo?.country) {
        return browserInfo?.country;
      }

      return defaultCountry.value;
    });

    const visitorFinalCountry = computed<string>(() => {
      if (visitorData.value?.country) {
        if (availableCountries.value.includes(visitorData.value?.country)) {
          return visitorData.value?.country;
        }
      } else if (browserInfo?.country) {
        if (availableCountries.value.includes(browserInfo?.country)) {
          return browserInfo?.country;
        }
      }

      return defaultCountry.value;
    });

    const visitorFinalLanguage = computed<string>(() => {
      if (import.meta.client) {
        if (currentLocale()) {
          return currentLocale();
        }
      }

      return browserInfo?.language
        ? browserInfo?.language.toLowerCase()
        : useRuntimeConfig().public.brand.language;
    });

    const visitorFinalTimezone = computed<string>(() => {
      return browserInfo?.timezone
        ? browserInfo?.timezone
        : useRuntimeConfig().public.brand.timezone;
    });

    return {
      allCountriesList,
      visitorData,
      visitorOriginalCountry,
      visitorFinalCountry,
      visitorFinalLanguage,
      visitorFinalTimezone,
      getCountries,
      getVisitorData,
    };
  },

  storeConfig,
);
